/** Aux info sync — record review. One tab per record kind (grouped by
 *  section), a server-paginated grid per tab with columns served by the
 *  backend kind registry, per-record edit drawer, manual additions, and
 *  the approval bar.
 *
 *  Selection semantics: the server computes per-record defaults
 *  (new/changed → on, existing → off, ops-decision kinds → off). The page
 *  stages OVERRIDES only and approves with `default_overrides`, so
 *  untouched records — including ones on pages never opened — follow the
 *  server default. "Apply defaults" with nothing staged sends `{}`.
 */
import AddIcon from '@mui/icons-material/Add';
import LaunchIcon from '@mui/icons-material/Launch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as auxSyncApi from '@/api/auxSync';
import type { AuxKindSpec, AuxRecordSummary } from '@/api/auxSync';
import * as bgtasksApi from '@/api/bgtasks';
import { isActiveState, TASK_STATE } from '@/api/bgtasks';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { TaskProgressCard } from '@/features/bgtasks/TaskProgressCard';

import { AuxSyncResultPanel } from './AuxSyncResultPanel';
import {
  noChangesReason,
  offersChanges,
  orderKindTabs,
  pendingChanges,
  SECTION_LABEL,
  visibleKindTabs,
} from './kindTabs';
import { AddRecordForm, RecordDetailDrawer } from './RecordDetailDrawer';
import {
  buildApprovalPayload,
  effectiveData,
  hasLocalChanges,
  isSelected,
  stillMissing,
  useReviewState,
} from './useReviewState';

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  new: 'success',
  changed: 'warning',
  existing: 'default',
};

export function AuxSyncReviewPage() {
  const { taskId = '' } = useParams<{ taskId: string }>();
  const { show } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const review = useReviewState();

  const [activeKind, setActiveKind] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>(['new', 'changed']);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 0, pageSize: 50 });
  const [detailKey, setDetailKey] = useState<string | null>(null);
  const [addFor, setAddFor] = useState<AuxKindSpec | null>(null);
  const [confirm, setConfirm] = useState<'apply' | 'skip-all' | 'cancel' | null>(null);

  const taskQuery = useQuery({
    queryKey: ['bgtask', taskId],
    queryFn: () => bgtasksApi.retrieve(taskId),
    refetchInterval: (q) => (q.state.data && isActiveState(q.state.data.state) ? 5_000 : false),
  });
  const task = taskQuery.data;

  const recordsQuery = useQuery({
    queryKey: [
      'aux-sync',
      'records',
      taskId,
      activeKind,
      statusFilter.join(','),
      search,
      pagination.page,
      pagination.pageSize,
    ],
    queryFn: () =>
      auxSyncApi.listRecords(taskId, {
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        kind: activeKind ?? undefined,
        status: statusFilter.length ? statusFilter.join(',') : undefined,
        search: search || undefined,
      }),
    // The artifact is immutable once analyze stages it.
    staleTime: Infinity,
    retry: (failureCount, error) => {
      // 409 = still collecting/analyzing; the task poll flips us when staged.
      const status = (error as { response?: { status?: number } })?.response?.status;
      return status !== 409 && status !== 404 && failureCount < 2;
    },
  });
  const page = recordsQuery.data;

  // The records artifact lands exactly when the task parks in
  // AWAITING_APPROVAL — the grid query 409'd until then, so kick it once the
  // polled task state flips (no manual reload).
  useEffect(() => {
    if (!page && task && task.state !== TASK_STATE.RUNNING && task.state !== TASK_STATE.PENDING) {
      void queryClient.invalidateQueries({ queryKey: ['aux-sync', 'records', taskId] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.state]);

  const kinds = useMemo(() => orderKindTabs(visibleKindTabs(page?.kinds ?? [])), [page?.kinds]);
  const specByCode = useMemo(
    () => Object.fromEntries((page?.kinds ?? []).map((k) => [k.code, k])),
    [page?.kinds],
  );

  // Land on the first tab that needs review (the sort already puts those
  // first; being explicit keeps the default independent of the ordering).
  const effectiveKind =
    activeKind ?? kinds.find(offersChanges)?.code ?? kinds[0]?.code ?? null;
  const spec = effectiveKind ? (specByCode[effectiveKind] ?? null) : null;

  const rows = useMemo(
    () => (effectiveKind === page?.results?.[0]?.kind || !page ? (page?.results ?? []) : []),
    [page, effectiveKind],
  );
  const detailRow = rows.find((r) => r.key === detailKey) ?? null;

  const awaitingApproval = task?.state === TASK_STATE.AWAITING_APPROVAL;

  const approveMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) => bgtasksApi.approve(taskId, payload),
    onSuccess: async () => {
      show('Approved — applying the selection.', 'success');
      review.reset();
      await queryClient.invalidateQueries({ queryKey: ['bgtask', taskId] });
      await queryClient.invalidateQueries({ queryKey: ['bgtasks'] });
    },
    onError: async (e) => {
      show((e as Error).message, 'error');
      await queryClient.invalidateQueries({ queryKey: ['bgtask', taskId] });
    },
  });
  const cancelMut = useMutation({
    mutationFn: () => bgtasksApi.cancel(taskId),
    onSuccess: async (r) => {
      show(r.message, 'info');
      await queryClient.invalidateQueries({ queryKey: ['bgtask', taskId] });
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  const selectionModel = useMemo<GridRowSelectionModel>(
    () => rows.filter((r) => isSelected(review.state, r)).map((r) => r.key),
    [rows, review.state],
  );

  const columns = useMemo<GridColDef<AuxRecordSummary>[]>(() => {
    if (!spec) return [];
    const dataColumns: GridColDef<AuxRecordSummary>[] = spec.columns.map((col) => ({
      field: `data_${col.field}`,
      headerName: col.label,
      flex: 1,
      minWidth: col.type === 'boolean' ? 90 : 140,
      sortable: false,
      valueGetter: (_v, row) => {
        const value = effectiveData(review.state, row)[col.field];
        if (value == null) return '';
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
      },
    }));
    const lead: GridColDef<AuxRecordSummary>[] = [
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              size="small"
              variant="outlined"
              color={STATUS_COLOR[row.status] ?? 'default'}
              label={row.status}
            />
            {review.state.edits[row.key] && <Chip size="small" color="info" label="edited" />}
          </Stack>
        ),
      },
    ];
    const trail: GridColDef<AuxRecordSummary>[] = [
      {
        field: 'source',
        headerName: 'Source',
        width: 90,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>{row.source}</span>
            {row.issues.length > 0 && (
              <Tooltip title={row.issues.join(' · ')}>
                <WarningAmberIcon fontSize="small" color="warning" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ];
    if (spec.options.includes('link_user')) {
      trail.push({
        field: 'link_user',
        headerName: 'Create/link user',
        width: 130,
        sortable: false,
        renderCell: ({ row }) => {
          const alreadyLinked = Boolean(
            (row.options.link_user as { already_linked?: boolean } | undefined)?.already_linked,
          );
          const email = String(effectiveData(review.state, row).email ?? '');
          const on = Boolean(review.state.options[row.key]?.link_user);
          return (
            <Tooltip
              title={
                alreadyLinked
                  ? 'Already linked to a user'
                  : email
                    ? 'Create the staff account (or link an existing one by email) on apply'
                    : 'Needs an email — edit the record first'
              }
            >
              <span>
                <Switch
                  size="small"
                  checked={on}
                  disabled={!awaitingApproval || alreadyLinked || !email}
                  onChange={(e) => review.setOption(row.key, 'link_user', e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
              </span>
            </Tooltip>
          );
        },
      });
    }
    return [...lead, ...dataColumns, ...trail];
  }, [spec, review, awaitingApproval]);

  const summary = page?.summary;
  const overrideOn = Object.values(review.state.overrides).filter(Boolean).length;
  const overrideOff = Object.values(review.state.overrides).filter((v) => !v).length;
  const probesFailed = (summary?.sampling.probes ?? []).filter((p) => !p.ok);

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="h5">
          Review auxiliary records <HelpLink story="aux-sync" />
        </Typography>
        {task && (
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {task.task_label}
            {task.tour_operator ? ` · ${task.tour_operator.name}` : ''}
          </Typography>
        )}
        <Button size="small" onClick={() => navigate(`/bgtasks/${taskId}`)} endIcon={<LaunchIcon />}>
          Open task (logs &amp; artifacts)
        </Button>
      </Stack>

      {summary && (
        <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>{summary.total}</strong> records
            {summary.by_status.new ? ` · ${summary.by_status.new} new` : ''}
            {summary.by_status.changed ? ` · ${summary.by_status.changed} changed` : ''}
            {summary.by_status.existing ? ` · ${summary.by_status.existing} already configured` : ''}
          </Typography>
          {summary.sampling.bookings_sampled && (
            <Typography variant="body2">
              {Object.values(summary.sampling.bookings_sampled).reduce((a, b) => a + b, 0)}{' '}
              bookings sampled
            </Typography>
          )}
        </Stack>
      )}
      {probesFailed.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {probesFailed.length} vendor probe(s) failed — results may be partial:
          </Typography>
          {probesFailed.slice(0, 6).map((p) => (
            <Typography key={p.name} variant="body2" sx={{ fontFamily: 'monospace' }}>
              {p.name}: {p.error ?? 'unknown error'}
            </Typography>
          ))}
          {probesFailed.length > 6 && (
            <Typography variant="body2">…and {probesFailed.length - 6} more — open the task logs.</Typography>
          )}
        </Alert>
      )}
      {task &&
        (task.state === TASK_STATE.RUNNING || task.state === TASK_STATE.PENDING) &&
        !page && (
          <Box sx={{ mb: 2, maxWidth: 720 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Collecting &amp; analyzing — records appear here automatically once staged.
            </Alert>
            <TaskProgressCard
              taskId={taskId}
              onTerminal={() =>
                queryClient.invalidateQueries({ queryKey: ['aux-sync', 'records', taskId] })
              }
            />
          </Box>
        )}
      {task && task.state === TASK_STATE.FAILED && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The task failed{task.error_message ? `: ${task.error_message}` : ''}. Records are read-only.
        </Alert>
      )}
      {task && task.state === TASK_STATE.COMPLETED && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <AuxSyncResultPanel taskId={taskId} />
          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => navigate(`/bgtasks/${taskId}`)} endIcon={<LaunchIcon />}>
              Open task (logs &amp; artifacts)
            </Button>
          </Box>
        </Paper>
      )}

      {kinds.length > 0 && (
        <Tabs
          value={effectiveKind ?? false}
          onChange={(_e, v) => {
            setActiveKind(v);
            setPagination((p) => ({ ...p, page: 0 }));
            setSearch('');
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1 }}
        >
          {kinds.map((k) => {
            const changes = pendingChanges(k);
            return (
              <Tab
                key={k.code}
                value={k.code}
                // Greyed, never disabled: an addable kind is where a manual
                // record gets added, an all-existing kind is how you confirm
                // what is already configured, and info kinds are browsable.
                // The chip (bare total, no "n/" numerator) carries the same
                // signal for anyone who cannot see the dimming.
                sx={changes ? undefined : { color: 'text.disabled', opacity: 0.65 }}
                title={changes ? undefined : noChangesReason(k)}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>{`${SECTION_LABEL[k.section]} · ${k.label}`}</span>
                    <Chip
                      size="small"
                      variant={changes ? 'filled' : 'outlined'}
                      color={changes ? 'primary' : 'default'}
                      label={changes ? `${changes}/${k.count}` : k.count}
                    />
                  </Stack>
                }
              />
            );
          })}
        </Tabs>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          value={statusFilter}
          onChange={(_e, v: string[]) => {
            setStatusFilter(v);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
        >
          <ToggleButton value="new">new</ToggleButton>
          <ToggleButton value="changed">changed</ToggleButton>
          <ToggleButton value="existing">existing</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Search records…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
          sx={{ width: 240 }}
        />
        <Box sx={{ flex: 1 }} />
        {awaitingApproval && spec?.addable && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAddFor(spec)}>
            Add {spec.label.replace(/s$/i, '').toLowerCase()}
          </Button>
        )}
      </Stack>

      {review.state.added.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
          {review.state.added.map((a, i) => (
            <Chip
              key={i}
              size="small"
              color="info"
              label={`+ ${a.kind}: ${String(a.data.name ?? a.data.product_id ?? a.data.path ?? '')}`}
              onDelete={() => review.removeAdded(i)}
            />
          ))}
        </Stack>
      )}

      {/* A tab with no pending changes is reachable and worth opening (that
          is where you confirm what is already configured, or add a manual
          record) — but the default new+changed filter hides every row on
          it, which reads as a broken tab. Say so, and offer the fix. */}
      {spec && page && page.count === 0 && spec.count > 0 && !statusFilter.includes('existing') && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={
            <Button
              size="small"
              onClick={() => {
                setStatusFilter(['new', 'changed', 'existing']);
                setPagination((p) => ({ ...p, page: 0 }));
              }}
            >
              Show all
            </Button>
          }
        >
          {spec.count} record(s) here, all already configured — hidden by the status filter.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: 520, mb: 2 }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row) => row.key}
          loading={recordsQuery.isLoading}
          checkboxSelection={awaitingApproval}
          isRowSelectable={({ row }) => stillMissing(review.state, row).length === 0}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => {
            const next = new Set(model.map(String));
            for (const row of rows) {
              const nowSelected = next.has(row.key);
              if (nowSelected !== isSelected(review.state, row)) {
                review.toggle(row, nowSelected);
              }
            }
          }}
          keepNonExistentRowsSelected
          pagination
          paginationMode="server"
          rowCount={page?.count ?? 0}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[25, 50, 100, 200]}
          onRowClick={({ row }) => setDetailKey(row.key)}
          disableRowSelectionOnClick
          density="compact"
        />
      </Paper>

      {awaitingApproval && (
        <Paper
          variant="outlined"
          sx={{ position: 'sticky', bottom: 0, p: 1.5, display: 'flex', alignItems: 'center', gap: 2, zIndex: 2 }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            Server defaults apply to untouched records
            {overrideOn ? ` · ${overrideOn} forced on` : ''}
            {overrideOff ? ` · ${overrideOff} forced off` : ''}
            {Object.keys(review.state.edits).length
              ? ` · ${Object.keys(review.state.edits).length} edited`
              : ''}
            {review.state.added.length ? ` · ${review.state.added.length} added` : ''}
            {Object.keys(review.state.options).length
              ? ` · ${Object.keys(review.state.options).length} user link(s)`
              : ''}
          </Typography>
          <Button
            variant="text"
            color="inherit"
            onClick={() => setConfirm('skip-all')}
            disabled={approveMut.isPending}
          >
            Skip all &amp; finish
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirm('cancel')}
            disabled={cancelMut.isPending}
          >
            Cancel task
          </Button>
          <Button variant="contained" onClick={() => setConfirm('apply')} disabled={approveMut.isPending}>
            {hasLocalChanges(review.state) ? 'Apply selection' : 'Apply defaults'}
          </Button>
        </Paper>
      )}

      <RecordDetailDrawer
        taskId={taskId}
        tenantId={task?.tour_operator?.id ?? null}
        row={detailRow}
        spec={detailRow ? (specByCode[detailRow.kind] ?? null) : null}
        editable={Boolean(awaitingApproval)}
        localEdit={detailRow ? review.state.edits[detailRow.key] : undefined}
        onSaveEdit={review.edit}
        onClearEdit={review.clearEdit}
        onClose={() => setDetailKey(null)}
      />

      <Drawer anchor="right" open={Boolean(addFor)} onClose={() => setAddFor(null)}>
        {addFor && (
          <AddRecordForm
            spec={addFor}
            onSubmit={(data) => {
              review.add({ kind: addFor.code, restech: page?.integration ?? null, data });
              setAddFor(null);
            }}
            onCancel={() => setAddFor(null)}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={confirm === 'apply'}
        title="Apply the selection?"
        message={
          `Selected records are materialized into ${task?.tour_operator?.name ?? 'the tenant'}: ` +
          'tags are upserted and config keys patched. Re-running the sync later is safe — ' +
          'applied records come back as "existing".'
        }
        confirmLabel="Apply"
        onConfirm={() => {
          setConfirm(null);
          approveMut.mutate(buildApprovalPayload(review.state));
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'skip-all'}
        title="Finish without applying?"
        message="Nothing is materialized; the extracted records stay on the task for reference."
        confirmLabel="Skip all"
        destructive
        onConfirm={() => {
          setConfirm(null);
          approveMut.mutate({ selected_ids: [] });
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        title="Cancel this task?"
        message="The task and its staged records are discarded."
        confirmLabel="Cancel task"
        destructive
        onConfirm={() => {
          setConfirm(null);
          cancelMut.mutate();
        }}
        onCancel={() => setConfirm(null)}
      />
    </Box>
  );
}
